<template>
  <!-- <div class="san-instant-search fit"> -->
  <slot
    :search-client="searchClient"
    :instant-search-instance="instantSearchInstance"
  />
  <!-- </div> -->
</template>

<script>
const cache = new Set()
const warn = message => {
  if (cache.has(message)) return
  cache.add(message)
  console.warn(message)
}
const oldApiWarning = `Vue InstantSearch: You used the prop api-key or app-id.
These have been replaced by search-client.

See more info here: https://www.algolia.com/doc/api-reference/widgets/instantsearch/vue/#widget-param-search-client`
</script>

<script setup>
import { ref, watch, onMounted, nextTick, onBeforeUnmount, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import instantsearch from 'instantsearch.js/es'
import { INSTANTSEARCH_FUTURE_DEFAULTS } from 'instantsearch.js/es/lib/InstantSearch'
import TISAdapter from 'typesense-instantsearch-adapter/src/TypesenseInstantsearchAdapter'
import { isEmpty } from 'lodash'

const props = defineProps ({
  insightsClient: { type: Function, default: undefined },
  indexName: { type: String, required: true },
  compositionID: { type: String, default: undefined },
  insights: {
    default: undefined,
    validator: value => typeof value === 'undefined' || typeof value === 'boolean' || typeof value === 'object'
  },
  stalledSearchDelay: { type: Number, default: undefined },
  onStateChange: { type: Function, default: undefined },
  initialUiState: { type: Object, default: undefined },
  apiKey: {
    type: String,
    default: undefined,
    validator: value => {
      if (value) warn(oldApiWarning)
      return false
    }
  },
  appId: {
    type: String,
    default: undefined,
    validator: value => {
      if (value) warn(oldApiWarning)
      return false
    }
  },
  middlewares: { type: Array, default: null },
  future: { type: Object, default: () => ({}) },
  searchParams: { type: Object, default: () => {} },
  minQueryLength: { type: Number, default: 0 }
})

const $route = useRoute()
const $router = useRouter()

const typesenseAdapter = new TISAdapter({
  server: {
    apiKey: import.meta.env.TYPESENSE_API_KEY,
    nodes: [
      {
        host: import.meta.env.TYPESENSE_HOST,
        port: import.meta.env.TYPESENSE_PORT,
        protocol: import.meta.env.TYPESENSE_PROTOCOL
      }
    ],
    cacheSearchResultsForSeconds: 2 * 60
  },
  additionalSearchParameters: {
    q: '',
    ...props.searchParams
  }
})

const searchClient = {
  ...typesenseAdapter.searchClient,
  search: async req => {
    const isEmptyQuery = req.every(({ params: { query } }) => !query?.trim())
    return isEmptyQuery
      ? await Promise.resolve({
          results: req.map(() => ({
            facets: {},
            facetStats: {},
            hits: [],
            hitsPerPage: 0,
            nbHits: 0,
            nbPages: 0,
            page: 0,
            processingTimeMS: 0,
            query: '',
            renderingContent: {},
            exhaustiveNbHits: false,
            params: ''
          }))
        })
      : await typesenseAdapter.searchClient.search(req)
  }
}

const routing = {
  router: {
    read: () => $route.query,
    write: query => $router.replace({ query }),
    createURL: () => $route.fullPath,
    onUpdate: cb => {
      routing.router._onPopState = () => cb(routing.router.read())
      window.addEventListener('popstate', routing.router._onPopState)
    },
    dispose: () => {
      window.removeEventListener('popstate', () => {})
    }
  },
  stateMapping: {
    stateToRoute: uiState => {
      // console.log('%cstateToRoute', 'color:orange', uiState)
      return {
        q: uiState[props.indexName].query
      }
    },
    routeToState: routeState => {
      // console.log('%crouteToState', 'color:lime', routeState)
      const state = !routeState || isEmpty(routeState)
        ? { [props.indexName]: { query: undefined } }
        : {
            [props.indexName]: {
              query: routeState.q
              // refinementList: {
              //   brand: routeState.brands && routeState.brands.split('~')
              // },
              // page: routeState.page // causing error in hits component
            }
          }
      return state
    }
  }
}

const onStateChange = ({ uiState, setUiState }) => {
  const { [props.indexName]: { query } } = uiState
  if (!query || query.length < props.minQueryLength) {
    setUiState({ [props.indexName]: { query: '' } }) // no search if less than 2 character
  } else {
    setUiState(uiState)
  }
  if (props.onStateChange) props.onStateChange({ uiState, setUiState })
}

const instantSearchInstance = ref(null)
const createInstantSearchInstance = () => instantsearch({
  searchClient,
  insightsClient: props.insightsClient,
  insights: props.insights,
  indexName: props.indexName,
  compositionID: props.compositionID,
  routing,
  stalledSearchDelay: props.stalledSearchDelay,
  onStateChange,
  initialUiState: props.initialUiState,
  future: {
    preserveSharedStateOnUnmount: true,
    persistHierarchicalRootCount: true,
    ...props.future
  }
})
instantSearchInstance.value = createInstantSearchInstance()

watch(() => searchClient.value, val => {
  warn(false, 'The `search-client` prop of `<san-instant-search>` changed between renders, which may cause more search requests than necessary. If this is an unwanted behavior, please provide a stable reference: https://www.algolia.com/doc/api-reference/widgets/instantsearch/vue/#widget-param-search-client')
  instantSearchInstance.value.helper.setClient(val).search()
  // instantSearchInstance.value.updateConfiguration(options) // Moguće da ovo radi
})

watch(() => props.indexName, val => {
  instantSearchInstance.value.helper.setIndex(val || '').search()
})

watch(() => props.compositionID, val => {
  instantSearchInstance.value.helper
    .setIndex(val || '')
    .search()
})

watch(() => props.stalledSearchDelay, val => {
  // private InstantSearch.js API:
  instantSearchInstance.value._stalledSearchDelay = val
})

watch(() => props.routing, () => {
  throw new Error(
    'routing configuration can not be changed dynamically at this point.'
    + '\n\n'
    + 'Please open a new issue: https://github.com/algolia/instantsearch/discussions/new?category=ideas&labels=triage%2cLibrary%3A+Vue+InstantSearch&title=Feature%20request%3A%20dynamic%20props'
  )
})

watch(() => props.onStateChange, () => {
  throw new Error(
    'onStateChange configuration can not be changed dynamically at this point.'
    + '\n\n'
    + 'Please open a new issue: https://github.com/algolia/instantsearch/discussions/new?category=ideas&labels=triage%2cLibrary%3A+Vue+InstantSearch&title=Feature%20request%3A%20dynamic%20props'
  )
})

watch(() => props.middlewares, (next, prev) => {
  (prev || [])
    .filter(middleware => (next || []).indexOf(middleware) === -1)
    .forEach(middlewareToRemove => {
      this.instantSearchInstance.unuse(middlewareToRemove)
    });

  (next || [])
    .filter(middleware => (prev || []).indexOf(middleware) === -1)
    .forEach(middlewareToAdd => {
      this.instantSearchInstance.use(middlewareToAdd)
    })
}, { immediate: true })

watch(() => props.future, val => {
  instantSearchInstance.value.future = Object.assign(INSTANTSEARCH_FUTURE_DEFAULTS, val)
})

onMounted(async () => {
  await nextTick()
  if (!instantSearchInstance.value.started) {
    instantSearchInstance.value.start()
  }
})
onBeforeUnmount(() => {
  if (instantSearchInstance.value.started) instantSearchInstance.value.dispose()

  // a hydrated instance will no longer be hydrated once disposed, and starts from scratch
  instantSearchInstance.value.__initialSearchResults = undefined
})

provide('$_ais_instantSearchInstance', instantSearchInstance)
</script>
